import React, { useState } from 'react';
import styled from 'styled-components';
import Button from '../Button';
import { FaGraduationCap, FaBook, FaDesktop, FaUserGraduate } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import AnimatedSection from '../Animation/AnimatedSection';

const TrainingSection = styled.section`
  padding: 6rem 0;
  background-color: ${props => props.theme.colors.secondary};
  position: relative;
  overflow: hidden;
`;

const TrainingContainer = styled(motion.div)`
  background-color: ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.large};
  padding: 3rem;
  color: ${props => props.theme.colors.white};
  position: relative;
  overflow: hidden;
  box-shadow: ${props => props.theme.boxShadow};
`;

const IconBackground = styled(motion.div)`
  position: absolute;
  right: -50px;
  bottom: -50px;
  opacity: 0.1;
  font-size: 15rem;
  z-index: 1;
`;

const TrainingContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 600px;
  
  @media screen and (max-width: 768px) {
    text-align: center;
  }
`;

const TrainingTitle = styled(motion.h2)`
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.white};
  font-style: italic;

  hyphens: auto;

  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.grayMedium};
    margin-top: 1rem;

    @media screen and (max-width: 768px) {
      margin-left: auto;
      margin-right: auto;
    }
  }
`;

const TrainingDescription = styled(motion.p)`
  color: ${props => props.theme.colors.grayLight};
  margin-bottom: 2rem;
  text-align: justify;
  text-justify: inter-word;
`;

const ButtonWrapper = styled(motion.div)`
  display: inline-block;
  
  @media screen and (max-width: 768px) {
    display: block;
    text-align: center;
  }
`;

const SectionTitle = styled(motion.h2)`
  color: ${props => props.theme.colors.primary};
  text-align: center;
  position: relative;
  padding-bottom: 1rem;
  margin-bottom: 3rem;
  text-transform: uppercase;
  font-style: italic;
  
  &:after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.grayMedium};
  }
`;

const TrainingCards = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-top: 3rem;
  width: 100%;
  max-width: 100%;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Card = styled(motion.div)`
  background-color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: 1.5rem;
  text-align: center;
  box-shadow: ${props => props.theme.boxShadow};
`;

const CardIcon = styled(motion.div)`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.black} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  color: ${props => props.theme.colors.white};
  font-size: 1.5rem;
`;

const CardTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.75rem;
`;

const CardText = styled.p`
  color: ${props => props.theme.colors.grayMedium};
  font-size: 0.9rem;
  text-align: justify;
  text-justify: inter-word;
`;

const Training = () => {
  const [containerRef, containerInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  
  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  // eslint-disable-next-line
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const closeMenu = () => setIsMenuOpen(false);

  const scrollToSection = (id) => {
    const target = document.getElementById(id);
    if (!target) return;
    const headerOffset = 80;
    const elementPosition = target.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  };
  
  const trainingTypes = [
    {
      icon: <FaUserGraduate />,
      title: "Treinamentos Presenciais",
      description: "Capacitação na sua empresa com especialistas em certificação e eficiência energética."
    },
    {
      icon: <FaDesktop />,
      title: "Cursos Online",
      description: "Lançamento em breve: Plataforma digital com conteúdos especializados disponíveis 24 horas por dia."
    },
    {
      icon: <FaBook />,
      title: "Workshops Técnicos",
      description: "Eventos intensivos para aprofundamento em temas específicos da área."
    }
  ];
  
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.2 + i * 0.15,
        duration: 0.5
      }
    }),
    hover: {
      y: -10,
      transition: { duration: 0.3 }
    }
  };
  
  const iconVariants = {
    hidden: { scale: 0 },
    visible: {
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20,
        delay: 0.3
      }
    },
    hover: {
      rotate: 10,
      scale: 1.1,
      transition: { duration: 0.2 }
    }
  };
  
  return (
    <TrainingSection id="treinamentos">
      <div className="container">
        <SectionTitle
          ref={titleRef}
          initial={{ opacity: 0, y: 20 }}
          animate={titleInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          Capacitação Especializada
        </SectionTitle>
        
        <AnimatedSection animation="fadeIn" delay={0.2}>
          <TrainingContainer
            ref={containerRef}
            initial={{ opacity: 0, y: 30 }}
            animate={containerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
          >
            <IconBackground
              initial={{ rotate: -10, opacity: 0 }}
              animate={containerInView ? { rotate: 0, opacity: 0.1 } : { rotate: -10, opacity: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <FaGraduationCap />
            </IconBackground>
            
            <TrainingContent>
              <TrainingTitle
                initial={{ opacity: 0, x: -30 }}
                animate={containerInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                Treinamentos e Workshops
              </TrainingTitle>
              
              <TrainingDescription
                initial={{ opacity: 0 }}
                animate={containerInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                Oferecemos treinamentos presenciais e online para sua equipe técnica 
                sobre as principais normas e portarias relacionadas à certificação de 
                produtos eletrodomésticos.
              </TrainingDescription>
              
              <TrainingDescription
                initial={{ opacity: 0 }}
                animate={containerInView ? { opacity: 1 } : { opacity: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
              >
                Prepare sua equipe para lidar com questões técnicas, processos de 
                certificação e mantenha-se atualizado sobre as regulamentações mais recentes.
              </TrainingDescription>
              
              <ButtonWrapper
                initial={{ opacity: 0, y: 20 }}
                animate={containerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                <Button 
                  inverted
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href="#contato"
                  onClick={() => { closeMenu(); scrollToSection('contato'); }}
                >
                  Solicite os treinamentos
                </Button>
              </ButtonWrapper>
            </TrainingContent>
          </TrainingContainer>
          
          <TrainingCards>
            {trainingTypes.map((item, index) => (
              <Card
                key={index}
                custom={index}
                variants={cardVariants}
                initial="hidden"
                animate={containerInView ? "visible" : "hidden"}
                whileHover="hover"
              >
                <CardIcon
                  variants={iconVariants}
                  initial="hidden"
                  animate={containerInView ? "visible" : "hidden"}
                  whileHover="hover"
                >
                  {item.icon}
                </CardIcon>
                <CardTitle>{item.title}</CardTitle>
                <CardText>{item.description}</CardText>
              </Card>
            ))}
          </TrainingCards>
        </AnimatedSection>
      </div>
    </TrainingSection>
  );
};

export default Training;
